// trainingResourceAPI
//
// Cliente HTTP del modulo "Formacion K12" del panel admin.
// Consume /api/v1/training-resource (coleccion propia, separada de los
// recursos de Formacion de REVI).
//
// Recurso: { _id, title, description, type, audiences[], url, file, image,
// order, active, created_at, updated_at }. type: 'video' | 'pdf' | 'info'.
// audiences: 'teacher' | 'institution'.
//
// El backend K12 responde en la convencion { code, message, data }. Este
// cliente normaliza la lista a { resources, pagination } para que los
// componentes compartidos del panel sirvan a las dos verticales sin saber
// de que endpoint vienen.

import { ENV } from "../Utils";

const ROUTE = "training-resource";

function authHeaders(token, extra = {}) {
  return { Authorization: `Bearer ${token}`, ...extra };
}

async function parseResponse(response) {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }
  if (!response.ok) {
    const err = new Error(body?.message || `HTTP ${response.status}`);
    err.status = response.status;
    err.body = body;
    throw err;
  }
  return body;
}

// El backend espera multipart cuando hay archivos. Aceptamos un objeto plano
// con campos string + 2 posibles archivos (image, document) y construimos el
// FormData. Si NO hay archivos, igualmente mandamos multipart para mantener un
// solo code path (multer lo acepta).
function buildFormData(data) {
  const fd = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key === "image" || key === "document") {
      if (value instanceof File || value instanceof Blob) {
        fd.append(key, value);
      }
      return;
    }
    fd.append(key, value);
  });
  return fd;
}

export class TrainingResourceAPI {
  baseApi = ENV.BASE_API;

  // Lista paginada. type y audience opcionales; search libre.
  async list(token, { page = 1, limit = 12, type, search, audience } = {}) {
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("limit", String(limit));
    if (type) qs.set("type", type);
    if (search) qs.set("search", search);
    if (audience) qs.set("audience", audience);
    const url = `${this.baseApi}/${ROUTE}?${qs.toString()}`;
    const response = await fetch(url, { headers: authHeaders(token) });
    const body = await parseResponse(response);
    return {
      resources: body?.data?.resources || [],
      pagination: body?.data?.pagination || null,
    };
  }

  async getById(token, id) {
    const url = `${this.baseApi}/${ROUTE}/${id}`;
    const response = await fetch(url, { headers: authHeaders(token) });
    return parseResponse(response);
  }

  // payload = { title, description, type, audiences (CSV), url?, order?,
  //             image?: File, document?: File }
  async create(token, payload) {
    const url = `${this.baseApi}/${ROUTE}`;
    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders(token),
      body: buildFormData(payload),
    });
    return parseResponse(response);
  }

  // payload solo con los campos a actualizar. Para sustituir el PDF basta con
  // incluir un nuevo File en 'document'; el controller borra el anterior.
  async update(token, id, payload) {
    const url = `${this.baseApi}/${ROUTE}/${id}`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: authHeaders(token),
      body: buildFormData(payload),
    });
    return parseResponse(response);
  }

  async remove(token, id) {
    const url = `${this.baseApi}/${ROUTE}/${id}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    return parseResponse(response);
  }
}
